import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    checkVisibilityRules,
    IAttribute,
    IBinding,
    ImagePermissionType,
    IPrintedImage,
    mapByKey,
} from "constants/product-customizations/customization";
import {
    TCustomizationSelection,
    CustomizationSelectionType,
    TPrintedImageCustomizationSelection,
    TMetaCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { themeOptions } from "styles/theme";
import Uploader from "../../product-media/components/uploader";
import _ from "lodash";
import ValidationError from "../../upload/validation-error";
import { format } from "lib/globalize-util";

import { FormatMessage } from "react-globalize";
import { DATATEST } from "constants/integration";
import type { IScope } from "reducers/scope";
import IconButton from "@material-ui/core/IconButton";
import {
    addImageToList,
    createCustomizationImage,
    resetUploadedImage,
    setSearching,
} from "actions/customization-actions";
import { connect } from "react-redux";
import { ICustomizationImageEntity } from "epics/customizations-epic";
import NotificationError from "../../notification-banners";
import TrashCanIcon from "../../icons/trash-can";
import UploadIcon from "../../icons/upload";
import Collapse from "@material-ui/core/Collapse";
import { IStore } from "reducers";
import { ChildAttributes } from "./child-attributes";
import { usePrintType } from "./printed-attribute";
import { TabsManager } from "./tabs-manager";
import { getInitialImageGroup, getCanUploadImages, getMetaNestedValues } from "./helpers";
import { PrintedAttributePrintTypes } from "./print-types";
import me from "models/me";
import { IProduct } from "records/item/item";
import { DropShadow } from "./customization-image";
import { IProductCustomizationImageType } from "records/brand/brand";

const MAX_IMAGE_SIZE = 10;

export interface IPrintedImageProps {
    product: IProduct;
    attribute: IAttribute;
    selected: TPrintedImageCustomizationSelection;
    types: string[];
    image: IPrintedImage;
    onChange?: (value: TPrintedImageCustomizationSelection) => void;
    isRequired?: boolean;
    attributeId: string;
    label: string;
    target?: IBinding;
    readonly?: boolean;
    onValidationError: (attributeId: string, message: string | null) => void;
    childAttributes?: IAttribute[];
    companyMerchantId?: string;
}

export interface IPrintedImageStateProps {
    uploadedImagePayload: ICustomizationImageEntity;
    isUploading: boolean;
    existingImages: Array<ICustomizationImageEntity | null>;
    isFetchingExistingImages: boolean;
    imagesError: string | Error | undefined;
    scope: IScope;
    imageTypes: IProductCustomizationImageType[];
}

export interface IPrintedImageDispatchProps {
    onCreateImage: (params: { uploadId: string; filename: string; mimetype: string; forMerchantIds: string[] }) => void;
    onResetUploadedImage: () => void;
    onAddImageToList: (image: ICustomizationImageEntity) => void;
    onSearchingStatusChanged: (value: boolean) => void;
}

export type IImageAttributes = Pick<
    TPrintedImageCustomizationSelection,
    "value" | "displayValue" | "imageFileName" | "imageAttributes"
>;

export type TPrintedImageAttributeProps = IPrintedImageProps & IPrintedImageDispatchProps & IPrintedImageStateProps;
export const PrintedImageAttributeContainer: React.FC<TPrintedImageAttributeProps> = ({
    selected,
    types,
    image,
    attributeId,
    label,
    target,
    onChange,
    scope,
    onCreateImage,
    uploadedImagePayload,
    isUploading,
    onResetUploadedImage,
    readonly,
    onValidationError,
    isFetchingExistingImages,
    onAddImageToList,
    childAttributes,
    companyMerchantId,
    product,
    attribute,
    imageTypes,
}) => {
    const classes = useStyles();
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<ICustomizationImageEntity | null>(null);

    const [nestedValues, setNestedValues] = useState<TMetaCustomizationSelection[]>([]);
    const [selectedPrintType, setSelectedPrintType] = usePrintType(selected);
    const { permissions = [] } = image;
    const [selectedTab, setSelectedTab] = useState<number>(getInitialImageGroup(permissions));

    const errorMessage = useMemo(
        () => format("There were issues with the image you uploaded. Make sure they are the correct file type/size."),
        [],
    );

    const isBuyerImagesSelected = selectedTab === ImagePermissionType.BUYER;
    const hasUploadImagePermission = useMemo(
        () => getCanUploadImages(selectedTab, permissions, me),
        [selectedTab, permissions],
    );

    const activeImageTypes = useMemo(() => imageTypes.filter((type) => !type.is_archived), [imageTypes]);

    const canUploadImage = !!activeImageTypes.length && hasUploadImagePermission;
    const selectedNestedValues = selected.nestedValues;
    const childAttributesMap = useMemo(() => mapByKey(childAttributes ?? [], "_id"), [childAttributes]);

    useEffect(() => {
        setNestedValues(getMetaNestedValues(nestedValues, selectedNestedValues, readonly, childAttributes));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childAttributes, selectedNestedValues, nestedValues?.length, readonly]);

    const filterNestedValues = useCallback(
        (newAttribute: TPrintedImageCustomizationSelection, newValues: TMetaCustomizationSelection[]) =>
            newValues.filter((v) =>
                checkVisibilityRules(newAttribute, childAttributesMap.get(v.attributeId)?.metadata?.visibilityRules),
            ),
        [childAttributesMap],
    );

    const handleChange = useCallback(
        (
            imageAttributes: IImageAttributes | TPrintedImageCustomizationSelection,
            printType: string,
            nestedValues: TMetaCustomizationSelection[] | undefined = [],
        ): void => {
            const attributeValue: TCustomizationSelection = {
                type: CustomizationSelectionType.PRINTED_IMAGE,
                attributeId,
                label,
                externalId: selected.externalId,
                merchantId: companyMerchantId,
                value: imageAttributes.value,
                displayValue: imageAttributes.displayValue,
                imageFileName: imageAttributes.imageFileName,
                imageAttributes: imageAttributes.imageAttributes,
                printType,
                target,
                nestedValues: [],
            };

            if (nestedValues.length !== 0) {
                attributeValue.nestedValues = filterNestedValues(attributeValue, nestedValues);
            }

            onChange?.(attributeValue);
        },
        [attributeId, selected.externalId, filterNestedValues, label, onChange, target, companyMerchantId],
    );

    const handleImagesChange = useCallback(
        (image: ICustomizationImageEntity): void => {
            const imageAttributes = extractImageAttributes(image);
            handleChange(imageAttributes, selectedPrintType, nestedValues);
        },
        [selectedPrintType, nestedValues, handleChange],
    );

    useEffect(() => {
        if (uploadedImagePayload) {
            onAddImageToList(uploadedImagePayload);
            onResetUploadedImage();
            const uploadedImageAttributes = extractImageAttributes(uploadedImagePayload);
            handleChange(uploadedImageAttributes, selectedPrintType, nestedValues);
        }
    }, [onAddImageToList, onResetUploadedImage, uploadedImagePayload, nestedValues, handleChange, selectedPrintType]);

    const onNestedValuesChanged = useCallback(
        (newNestedValues: TMetaCustomizationSelection[]) => {
            setNestedValues(newNestedValues);
            handleChange(selected, selectedPrintType, newNestedValues);
        },
        [selected, handleChange, selectedPrintType],
    );

    const handleUpload = useCallback(
        (results: { id: string; filename: string; content_type: string }) => {
            if (_.isEmpty(results)) {
                return null;
            }

            if (results.id) {
                onCreateImage({
                    uploadId: results.id,
                    filename: results.filename,
                    mimetype: results.content_type,
                    forMerchantIds: isBuyerImagesSelected && companyMerchantId ? [companyMerchantId] : [],
                });
            }
        },
        [onCreateImage, isBuyerImagesSelected, companyMerchantId],
    );

    const handleRemoveSelectedImage = () => {
        const emptyImageAttributes = {
            value: "",
            displayValue: "",
            imageFileName: "",
        };

        handleChange(emptyImageAttributes, selectedPrintType, nestedValues);
    };

    const handleExpandAccordion = () => {
        setExpanded(!expanded);
    };

    const handleTypeChange = useCallback(
        (selectedType: string) => {
            setSelectedPrintType(selectedType);
            handleChange(selected, selectedType, nestedValues);
        },
        [selected, nestedValues, handleChange, setSelectedPrintType],
    );

    const handleReject = useCallback(() => setUploadError(errorMessage), [errorMessage]);
    const handleUploadStart = useCallback(() => setUploadError(null), []);

    const handleError = useCallback((error: ValidationError) => {
        setUploadError(error.message);
    }, []);

    const handleHideError = useCallback(() => setUploadError(null), []);

    return (
        <div className={classes.container}>
            <PrintedAttributePrintTypes
                types={types}
                printType={selectedPrintType}
                readonly={readonly}
                onChange={handleTypeChange}
            />
            {!readonly && (
                <TabsManager
                    selected={selected}
                    merchantId={companyMerchantId}
                    permissions={permissions}
                    onChange={handleImagesChange}
                    onSelectImage={setSelectedImage}
                    onTabChanged={setSelectedTab}
                    product={product}
                    attribute={attribute}
                    onRemoveSelectedImage={handleRemoveSelectedImage}
                />
            )}

            {!isFetchingExistingImages && (
                <div>
                    {!readonly && canUploadImage ? (
                        <div data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_CONTAINER}>
                            <div className={classes.uploadImageTitle}>{format("Upload Image")}:</div>
                            <div
                                data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_EXPAND_UPLOADER}
                                className={classes.uploadImageLink}
                                onClick={handleExpandAccordion}
                            >
                                <UploadIcon className={classes.uploadIcon} />
                                <div className={classes.uploadImageText}>{format("Upload Image")}</div>
                            </div>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <div
                                    data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_FILE_SPEC_ERROR}
                                    className={classes.error}
                                >
                                    <NotificationError message={uploadError} onClose={handleHideError} />
                                </div>

                                <div className={classes.uploadContainer}>
                                    <div className={classes.uploadWrapper}>
                                        <Uploader
                                            inputProps={{
                                                "data-test": DATATEST.CUSTOMIZATIONS.PRINTED_IMAGE_UPLOADER,
                                            }}
                                            multiple={false}
                                            accept={activeImageTypes.map((type) => type.accept_type).join(",")}
                                            onUpload={handleUpload as any} // TODO: typehack as Uploader is typed incorrectly
                                            onUploadStart={handleUploadStart}
                                            onUploadError={handleError}
                                            onDropReject={handleReject}
                                            maxSize={MAX_IMAGE_SIZE * 1024 * 1024}
                                            loading={loading || isUploading}
                                            scope={scope}
                                            onLoading={setLoading}
                                            style={styles.uploader}
                                        >
                                            <div className={classes.dragLabel}>
                                                <FormatMessage
                                                    path="Drag your image files here |br| or |span|click here|span| to select"
                                                    elements={{ br: <br />, span: <span className={classes.click} /> }}
                                                />
                                            </div>
                                        </Uploader>
                                    </div>
                                    <div>
                                        <div
                                            data-test={DATATEST.CUSTOMIZATIONS.PRINTED_IMAGE_FILE_TYPES}
                                            className={classes.imageFormatLabel}
                                        >
                                            <span className={classes.dot}></span>
                                            <span>{activeImageTypes.map((type) => type.name).join("/")}</span>
                                        </div>
                                        <div className={classes.imageFormatLabel}>
                                            <span className={classes.dot}></span>
                                            <span>
                                                {format("File size: |max| max", { max: `${MAX_IMAGE_SIZE} MB` })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Collapse>
                        </div>
                    ) : null}
                    {selected?.displayValue ? (
                        <div className={classes.imageDetails}>
                            <DropShadow shadowColor="gray" shadowRadius="1px">
                                <img
                                    data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_PREVIEW}
                                    src={selected?.displayValue}
                                    className={classes.imagePreview}
                                    alt={"Customization image preview"}
                                />
                            </DropShadow>
                            <div
                                data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_PREVIEW_NAME}
                                className={classes.imageDescription}
                            >
                                <div className={classes.imageName}>
                                    {selected?.imageFileName || selectedImage?.name}
                                </div>
                                {selected?.imageAttributes
                                    ?.filter(({ isVisible }) => isVisible)
                                    .map((attribute) => (
                                        <div key={attribute.attributeId} className={classes.imageAttribute}>
                                            <span className={classes.imageAttributeName}>{`${attribute.name}: `}</span>
                                            <span>{attribute.value}</span>
                                        </div>
                                    ))}
                            </div>
                            {!readonly && (
                                <IconButton
                                    data-test={DATATEST.CUSTOMIZATIONS.UPLOAD_IMAGE_DELETE_BUTTON}
                                    onClick={handleRemoveSelectedImage}
                                    className={classes.deleteIconButton}
                                >
                                    <TrashCanIcon stroke="#ff0505" className={classes.deleteIcon} />
                                </IconButton>
                            )}
                        </div>
                    ) : null}
                </div>
            )}
            {childAttributes && childAttributes.length > 0 && (
                <>
                    <div className={classes.divider} />
                    <ChildAttributes
                        childAttributes={childAttributes}
                        nestedValues={nestedValues}
                        selected={selected}
                        attributeId={attributeId}
                        readonly={readonly ?? false}
                        onChange={onNestedValuesChanged}
                        onValidationError={onValidationError}
                    />
                </>
            )}
        </div>
    );
};

const mapStateToProps = (state: IStore): IPrintedImageStateProps => {
    const { scope, customization } = state;
    const { imageUpload, existingImages, brandSettings } = customization;
    return {
        uploadedImagePayload: imageUpload?.payload,
        isUploading: imageUpload?.isUploading,
        existingImages: existingImages?.payload?.images,
        imagesError: existingImages?.error,
        isFetchingExistingImages: existingImages?.isFetching,
        scope,
        imageTypes: brandSettings?.imageTypes ?? scope.brand?.settings?.product_customizations?.image_types ?? [],
    };
};

const mapDispatchToProps = (dispatch: (...args: any[]) => void): IPrintedImageDispatchProps => ({
    onCreateImage: ({ uploadId, filename, mimetype, forMerchantIds }) =>
        dispatch(createCustomizationImage({ uploadId, filename, mimetype, forMerchantIds })),
    onResetUploadedImage: () => dispatch(resetUploadedImage()),
    onAddImageToList: (image) => dispatch(addImageToList(image)),
    onSearchingStatusChanged: (value) => dispatch(setSearching(value)),
});

export const PrintedImageAttribute = connect<IPrintedImageStateProps, IPrintedImageDispatchProps, IPrintedImageProps>(
    mapStateToProps,
    mapDispatchToProps,
)(PrintedImageAttributeContainer);

const useStyles = makeStyles({
    trashIcon: {
        "& svg": {
            width: 30,
            height: 30,
            color: "red",
        },
    },
    uploadContainer: {
        display: "flex",
        flexDirection: "row",
    },
    dot: {
        width: 8,
        height: 8,
        backgroundColor: themeOptions.palette.textColor,
        alignSelf: "center",
        borderRadius: "50%",
        marginInlineEnd: "10px",
    },
    accordionSummary: {
        margin: 0,
        padding: 0,
    },
    uploadIcon: {
        marginInlineEnd: 12,
        fill: themeOptions.palette.darkGrey,
    },
    deleteIcon: {
        float: "right",
        width: "2rem !important",
        height: "2rem !important",
    },
    deleteIconButton: {
        marginLeft: "auto",
        width: "20px",
        height: "20px",
    },
    uploadWrapper: {
        width: "200px",
        height: "150px",
        marginLeft: "20px",
    },
    imageFormatLabel: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        fontSize: 12,
        fontWeight: 700,
    },
    divider: {
        height: 1,
        width: "100%",
        marginBlock: 16,
        backgroundColor: themeOptions.palette.borderColor,
    },
    accordion: {
        backgroundColor: themeOptions.palette.lightestGrey,
        boxShadow: "none",
        borderRadius: "none",
        width: "100%",
        "& .MuiAccordionSummary-root:before": {
            backgroundColor: "red !important",
        },
    },
    uploadImageLink: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        marginBottom: "10px",
        cursor: "pointer",
    },
    uploadImageText: {
        fontSize: 15,
        textDecoration: "underline",
        color: themeOptions.palette.darkGrey,
    },
    uploadImageTitle: {
        color: themeOptions.palette.textColor,
        fontSize: 12,
        fontWeight: 700,
        marginBlockEnd: 8,
        marginBlockStart: 16,
    },
    imageDetails: {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        marginTop: "20px",
    },
    imageDescription: {
        marginLeft: "10px",
    },
    imageName: {
        fontWeight: "bold",
        fontSize: 14,
    },
    imageAttribute: {
        fontSize: 10,
    },
    imageAttributeName: {
        fontWeight: "bold",
    },
    imagePreview: {
        maxWidth: "100px",
        maxHeight: "80px",
        border: `1px solid ${themeOptions.palette.darkGrey}`,
    },
    container: {
        width: "100%",
    },
    click: {
        textDecoration: "underline",
    },
    dragLabel: {
        fontSize: 14,
        textAlign: "center",
        lineHeight: "1.8em",
        color: themeOptions.palette.darkGrey,
    },
    error: {
        margin: 10,
    },
    validationError: {
        fontSize: "14px",
        color: themeOptions.palette.error,
    },
    searchIcon: {
        position: "absolute",
        bottom: "18px",
        fontSize: "20px",
        left: "7px",
        color: themeOptions.palette.grey,
    },
});

const styles = {
    uploader: {
        width: "75%",
        height: "150px",
        backgroundColor: themeOptions.palette.lightGrey,
        border: `1px solid ${themeOptions.palette.darkGrey}`,
    },
    searchInput: {
        paddingLeft: "30px",
        height: "40px",
    },
};

const extractImageAttributes = (image: ICustomizationImageEntity): IImageAttributes => ({
    value: image.id,
    displayValue: image.url,
    imageFileName: image.name,
    imageAttributes: image.customizationAttributes,
});
